import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { ThirdPartyStatus } from '@risksmart-app/domain/src/types/consts/third-party-status';
import type { ThirdPartyType } from '@risksmart-app/domain/src/types/consts/third-party-type';
import type {
  CreateThirdPartyMutation,
  InsertThirdPartyInput,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { CreateThirdPartyDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import {
  toCriticality,
  toThirdPartyStatus,
  toThirdPartyType,
} from './thirdPartyEnums';

/**
 * Public input for the hook. Matches the third-party form call site where
 * Type/Status are plain strings (the select options ARE the enum values) and
 * Criticality is a coerced number. Everything else mirrors the GraphQL
 * InsertThirdPartyInput (required id arrays).
 */
type InsertThirdPartyInputShape = InsertThirdPartyInput;

/**
 * tRPC input type derived from GraphQL InsertThirdPartyInput.
 * Title/Type/Status are required by the tRPC router (Type/Status are required
 * native enums). `Criticality` is OPTIONAL — the column is a NULLABLE integer
 * with a CHECK 1..4, and the form offers a blank "-" option. Viewer arrays are
 * nullish for the tri-state (undefined = no change, [] = clear-all).
 */
export type InsertThirdPartyTRPCInput = {
  Title: string;
  Type: ThirdPartyType;
  Status: ThirdPartyStatus;
  Criticality?: number | null;
  Description?: string | null;
  CompanyName?: string | null;
  CompaniesHouseNumber?: string | null;
  Address?: string | null;
  CityTown?: string | null;
  Postcode?: string | null;
  Country?: string | null;
  PrimaryContactName?: string | null;
  ContactName?: string | null;
  ContactEmail?: string | null;
  CompanyDomain?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): CreateThirdPartyMutation => {
  return {
    insertThirdPartyApi: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertThirdParty = createMutationHook({
  name: 'insertThirdParty',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.thirdParty.insert.mutationOptions({ onSuccess }),
  mapInput: (
    variables: InsertThirdPartyInputShape
  ): InsertThirdPartyTRPCInput => ({
    ...variables,
    Type: toThirdPartyType(variables.Type),
    Status: toThirdPartyStatus(variables.Status),
    Criticality: toCriticality(variables.Criticality),
  }),
  invalidate: (trpc) => [
    trpc.frontend.thirdParty.register.queryKey(),
    trpc.frontend.thirdParty.getById.queryKey(),
  ],
  mapResponse: (result: { Id: string }): CreateThirdPartyMutation =>
    mapTrpcResponseToGraphQL(result),
  graphqlDocument: CreateThirdPartyDocument,
  graphqlVariables: (variables: InsertThirdPartyInputShape) => ({
    object: variables,
  }),
  evictFields: ['third_party'],
});
